import { FaExchangeAlt, FaUndo } from "react-icons/fa";

import { useCallback, useState, useEffect, useMemo } from "react";
import SearchBox from "../common/SearchBox";
import { useLoading } from "../common/LoadingContext";
import { useToast } from "../common/ToastContext";
import twilioSenderService from "../../services/twilioSenderService";
import UnclaimedProfileTable from "./UnclaimedProfileTable";
import RegisteredProfileTable from "./RegisteredProfileTable";
import { currentUserHasRole } from "../../services/userService";
import AccessDenied from "../common/AccessDenied";

const TwilioSenderDesktop = () => {
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "descending",
  });
  const [twilioSenders, setTwilioSenders] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [messagingServices, setMessagingServices] = useState([]);
  const [detailedSenders, setDetailedSenders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRegexMode, setIsRegexMode] = useState(false);
  const [view, setView] = useState("registered");
  const [accessDenied, setAccessDenied] = useState(null);
  const { setIsLoading } = useLoading();
  const { showToast } = useToast();

  const checkAccess = useCallback(async () => {
    try {
      const hasAccess = await currentUserHasRole("admin");
      setAccessDenied(!hasAccess);
    } catch (error) {
      console.error("Error checking user role:", error);
      setAccessDenied(true);
    }
  }, []);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const fetchTwilioSendersAndMessagingServices = async () => {
    setIsLoading(true);
    try {
      const senders = await twilioSenderService.getTwilioSenders();
      const profilesData = await twilioSenderService.getCustomerProfiles();
      const brandsData = await twilioSenderService.getBrands();
      const servicesData = await twilioSenderService.getMessagingServices();
      const sendersWithDetails = senders.map((sender) => {
        const service = servicesData.find(
          (service) => service.sid === sender.message_service_sid,
        );
        return {
          ...sender,
          messaging_service_name: service ? service.friendly_name : "N/A",
        };
      });
      setProfiles(profilesData);
      setBrands(brandsData);
      setTwilioSenders(senders);
      setMessagingServices(servicesData);
      setDetailedSenders(sendersWithDetails);
    } catch (error) {
      console.error("Error fetching Twilio senders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTwilioSendersAndMessagingServices();
  }, [setIsLoading]);

  const sortFromConfig = (sortableItems) => {
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  const sortedSenders = useMemo(() => {
    let sortableItems = [...detailedSenders];
    return sortFromConfig(sortableItems);
  }, [detailedSenders, sortConfig]);

  const unclaimedProfiles = profiles
    .filter((profile) => !profile.sender)
    .map((profile) => profile.profile);

  const sortedProfiles = useMemo(() => {
    let sortableItems = [...unclaimedProfiles];
    return sortFromConfig(sortableItems);
  }, [profiles, sortConfig]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = useCallback((term, regexMode) => {
    setSearchTerm(term);
    setIsRegexMode(regexMode);
  }, []);

  const getProfileFromSid = (sid) => {
    const match = profiles.find((p) => p.profile.sid === sid);
    return match ? match.profile : null;
  };

  const getMessagingServiceFromSid = (sid) => {
    const service = messagingServices.find((s) => s.sid === sid);
    return service || null;
  };

  const onClaim = async ({
    customerProfileSid,
    messagingServiceFriendlyName,
    phoneNumberSid,
  }) => {
    try {
      // Find the brand associated with this customer profile
      const brand = brands.find(
        (b) => b.customer_profile_bundle_sid === customerProfileSid,
      );
      if (!brand) {
        showToast(
          "No brand found for this customer profile. Cannot claim.",
          "error",
        );
        return;
      }

      const messagingService = await twilioSenderService.createMessagingService(
        {
          friendly_name: messagingServiceFriendlyName,
        },
      );
      await twilioSenderService.createSender({
        sender_name: messagingServiceFriendlyName,
        brand_sid: brand.sid,
        messaging_service_sid: messagingService.sid,
        customer_profile_sid: customerProfileSid,
        phone_number_sid: phoneNumberSid,
      });
      await fetchTwilioSendersAndMessagingServices();
      showToast("Profile claimed successfully", "info");
    } catch (error) {
      console.error("Error claiming profile:", error);
      showToast(`Error claiming profile: ${error}`, "error");
    }
  };

  const onClaimCnam = async (cnam) => {
    console.log("Claiming CNAM:", cnam);
  };

  const onRefreshSender = async (sender) => {
    try {
      setIsLoading(true);
      const updatedSender = await twilioSenderService.updateSenderStatus({
        twilio_sender_id: sender.id,
      });
      const updatedSenders = detailedSenders.map((s) =>
        s.id === updatedSender.id
          ? {
              ...s,
              a2p_10dlc_status: updatedSender.a2p_10dlc_status,
              campaign_status: updatedSender.campaign_status,
            }
          : s,
      );
      setDetailedSenders(updatedSenders);
    } catch (error) {
      console.error("Error refreshing sender data:", error);
      showToast(`Error refreshing sender data: ${error}`, "error");
    } finally {
      setIsLoading(false);
      showToast("Sender data refreshed successfully.");
    }
  };

  const handleRefreshAll = async () => {
    try {
      setIsLoading(true);
      const updatedSenders =
        await twilioSenderService.refreshAllSenderStatuses();
      const updatedSendersWithDetails = updatedSenders.map((updatedSender) => {
        const service = messagingServices.find(
          (service) => service.sid === updatedSender.message_service_sid,
        );
        return {
          ...updatedSender,
          messaging_service_name: service ? service.friendly_name : "N/A",
        };
      });
      setDetailedSenders(updatedSendersWithDetails);
    } catch (error) {
      console.error("Error refreshing all sender data:", error);
      showToast(`Error refreshing all sender data: ${error}`, "error");
    } finally {
      setIsLoading(false);
      showToast("All sender data refreshed successfully.");
    }
  };

  if (accessDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="overflow-hidden">
      {/*Search Box*/}
      <div className="absolute top-4 left-[50%] translate-x-[-50%] w-[500px] z-100">
        <div className="flex items-center justify-between">
          {view === "registered" && (
            <div
              className="flex items-center text-xs text-gray-300 hover:text-white cursor-pointer mr-2"
              title="Refresh all sender data"
              onClick={handleRefreshAll}
            >
              <FaUndo
                size={20}
                className="mr-2 hover:text-white cursor-pointer transition-colors duration-150"
              />
              Refresh All Senders
            </div>
          )}
          <SearchBox
            onSearch={handleSearch}
            placeholder={
              view === "registered"
                ? "Filter Twilio Senders ..."
                : "Filter Unclaimed Customer Profiles ..."
            }
            className="w-full"
          />

          <div
            className="flex items-center text-xs text-gray-300 hover:text-white cursor-pointer"
            onClick={() =>
              view === "registered"
                ? setView("unclaimed")
                : setView("registered")
            }
          >
            <FaExchangeAlt size={30} className="ml-4 " />
            <div className="ml-2 ">
              {view === "registered"
                ? "View Unclaimed Customer Profiles"
                : "View Registered Profiles"}
            </div>
          </div>
        </div>
      </div>

      {/*Body*/}
      <div className="flex flex-col h-full border border-transparent rounded-lg p-6 relative">
        <h1
          className="text-xl font-bold mb-6 text-gray-100"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {view === "registered"
            ? "Twilio Senders"
            : "Unclaimed Customer Profiles"}
        </h1>

        {view === "registered" ? (
          <RegisteredProfileTable
            senders={sortedSenders}
            sortConfig={sortConfig}
            handleSort={handleSort}
            searchTerm={searchTerm}
            isRegexMode={isRegexMode}
            getProfileFromSid={getProfileFromSid}
            getMessagingServiceFromSid={getMessagingServiceFromSid}
            onClaimCnam={onClaimCnam}
            onRefreshSender={onRefreshSender}
          />
        ) : (
          <UnclaimedProfileTable
            profiles={sortedProfiles}
            sortConfig={sortConfig}
            handleSort={handleSort}
            onClaim={onClaim}
            searchTerm={searchTerm}
            isRegexMode={isRegexMode}
          />
        )}

        <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden flex-grow"></div>
      </div>
    </div>
  );
};

export default TwilioSenderDesktop;
